import { Component, EventEmitter, Input, Output } from '@angular/core'
import { environment } from '@environments/environment'
import { ImageCropModalComponent } from '@app/features/shared/components/image-crop-modal/image-crop-modal.component'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'

export type ImageDimensions = {
    width: number,
    height: number,
}

export type ImageCropAspectRatioRange = {
    min: number,
    max: number,
    initial: number,
}

@Component({
    selector: 'app-image-upload',
    templateUrl: './image-upload.component.html',
})
export class ImageUploadComponent {

    @Input() imageId?: string | null
    @Input() imagePath = ''
    @Input() width = 168
    @Input() height = 168
    @Input() aspectFit = false
    @Input() disabled = false
    @Input() uploading = false
    @Input() inputId = 'image-upload'
    @Input() alt = 'Image'
    @Input() description?: string
    @Input() allowedImageDimensions: ImageDimensions[] = []
    @Input() cropImageDimensions?: ImageDimensions
    @Input() cropAspectRatioRange?: ImageCropAspectRatioRange

    @Output() upload = new EventEmitter<Event>()
    @Output() uploadFile = new EventEmitter<File>()
    @Output() remove = new EventEmitter<void>()

    protected readonly environment = environment
    protected validationError?: string

    constructor(
        private modalService: NgbModal
    ) {
    }

    async onFileSelected(event: Event) {
        const fileTarget = event.target as HTMLInputElement
        const file = fileTarget.files?.[0]
        this.validationError = undefined

        if (!file) {
            return
        }

        const validationError = await this.validateFile(file)
        if (validationError) {
            this.validationError = validationError
            fileTarget.value = ''
            return
        }

        if (this.cropImageDimensions) {
            await this.cropFile(file, fileTarget)
            return
        }

        this.upload.emit(event)
    }

    onRemove() {
        this.validationError = undefined
        this.remove.emit()
    }

    private async validateFile(file: File): Promise<string | null> {
        if (!this.hasValidationRules()) {
            return null
        }
        if (file.type && !file.type.startsWith('image/')) {
            return 'Choose an image file.'
        }

        let dimensions: ImageDimensions
        try {
            dimensions = await this.getImageDimensions(file)
        } catch {
            return 'This image could not be read. Choose another image.'
        }

        if (!this.hasAllowedDimensions(dimensions)) {
            return this.allowedDimensionsMessage(dimensions)
        }

        return null
    }

    private hasValidationRules(): boolean {
        return this.allowedImageDimensions.length > 0 || !!this.cropImageDimensions
    }

    private hasAllowedDimensions(dimensions: ImageDimensions): boolean {
        if (this.cropImageDimensions) {
            return dimensions.width >= this.cropImageDimensions.width &&
                dimensions.height >= this.cropImageDimensions.height
        }
        if (this.allowedImageDimensions.length === 0) {
            return true
        }
        return this.allowedImageDimensions.some(allowedDimensions =>
            dimensions.width === allowedDimensions.width &&
            dimensions.height === allowedDimensions.height
        )
    }

    private allowedDimensionsMessage(dimensions: ImageDimensions): string {
        if (this.cropImageDimensions) {
            return `This image is ${dimensions.width} x ${dimensions.height} px. ` +
                `Choose an image at least ${this.cropImageDimensions.width} x ${this.cropImageDimensions.height} px.`
        }
        return `This image is ${dimensions.width} x ${dimensions.height} px. ` +
            'Choose one of the exact sizes above.'
    }

    private async cropFile(file: File, fileTarget: HTMLInputElement) {
        const modal = this.modalService.open(
            ImageCropModalComponent,
            {
                backdrop: 'static',
                centered: true,
                size: 'xl',
            }
        )
        const component = modal.componentInstance as ImageCropModalComponent
        component.file = file
        component.cropDimensions = this.cropImageDimensions!
        component.aspectRatioRange = this.cropAspectRatioRange
        const croppedFile = await modal.result.catch(() => null) as File | null
        fileTarget.value = ''
        if (croppedFile) {
            this.uploadFile.emit(croppedFile)
        }
    }

    private getImageDimensions(file: File): Promise<ImageDimensions> {
        return new Promise((resolve, reject) => {
            const imageUrl = URL.createObjectURL(file)
            const image = new Image()
            image.onload = () => {
                URL.revokeObjectURL(imageUrl)
                resolve({
                    width: image.naturalWidth,
                    height: image.naturalHeight,
                })
            }
            image.onerror = () => {
                URL.revokeObjectURL(imageUrl)
                reject(new Error('Image failed to load'))
            }
            image.src = imageUrl
        })
    }
}
